import React from 'react';
import { useLocationContext } from '../context/LocationContext';
import { useEntityStats, type EntityKey } from '../context/EntityStatsContext';
import { crmLabels, visibleEntityKeys } from '../lib/crmLabels';
import { GhlMappingCleanup } from '../components/shared/GhlMappingCleanup';

interface DashboardProps {
    onNavigate: (key: EntityKey, statusFilter?: string) => void;
}

const ENTITY_ORDER: EntityKey[] = ['appointments', 'providers', 'status', 'services'];

const ENTITY_TITLES: Record<EntityKey, string> = {
    appointments: 'Appointments',
    providers: 'Providers',
    status: 'Status',
    services: 'Services',
};

const ENTITY_SUBS: Record<EntityKey, string> = {
    appointments: 'Type → calendar',
    providers: 'Practitioner → Advital user',
    status: 'EMR status → Advital status',
    services: 'Service → calendar',
};

const AV_EMR_ENTITY_SUBS: Record<EntityKey, string> = {
    appointments: 'Type → AV EMR service',
    providers: 'Practitioner → AV EMR provider',
    status: 'EMR status → AV EMR status',
    services: 'Service → calendar',
};

export const Dashboard: React.FC<DashboardProps> = ({ onNavigate }) => {
    const { currentLocation, loading: locationsLoading } = useLocationContext();
    const { stats, loading: statsLoading } = useEntityStats();
    const labels = crmLabels(currentLocation?.crm);
    const entityKeys = visibleEntityKeys(ENTITY_ORDER, currentLocation?.crm);
    const entitySubs = labels.isAvEmr ? AV_EMR_ENTITY_SUBS : ENTITY_SUBS;

    return (
        <div className="mapper-ui font-sans text-mapper-text max-w-[1100px] mx-auto px-10 pt-[34px] pb-[60px]">
            <h1 className="text-[26px] font-extrabold tracking-[-0.5px] mb-[5px]">Sync overview</h1>
            <p className="text-[14.5px] text-mapper-muted mb-[30px] max-w-[640px]">
                Advital Sync keeps appointments flowing both ways between your scheduling system (EMR) and Advital. To sync cleanly, each item type below must be matched between the two systems.
            </p>

            {!currentLocation ? (
                <div className="bg-mapper-white border border-mapper-border rounded-[18px] p-[30px_34px] text-[13.5px] text-mapper-muted">
                    {locationsLoading ? 'Loading locations…' : 'Select a location above to view its sync status.'}
                </div>
            ) : (
                <>
                    {/* Old GHL mappings after a switch to AV EMR (cleanup / restore) */}
                    <GhlMappingCleanup />

                    {/* SYNC FLOW DIAGRAM */}
                    <div className="bg-mapper-white border border-mapper-border rounded-[18px] px-[34px] py-[30px] mb-[30px]">
                        <div className="flex items-stretch gap-0">
                            {/* EMR node */}
                            <div className="flex-1 min-w-0 border-[1.5px] border-mapper-purple-border bg-mapper-purple-bg-2 rounded-[14px] p-4">
                                <div className="flex items-center gap-[10px] mb-1">
                                    <div className="w-[30px] h-[30px] rounded-lg bg-mapper-purple-bg flex items-center justify-center">
                                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#6A62B5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M4 3h11l5 5v13H4z" /><path d="M9 12h6M9 16h6M12 8.5V9" /><path d="M10.5 10.5h3" />
                                        </svg>
                                    </div>
                                    <div className="text-xs font-bold text-mapper-purple tracking-[0.4px]">EMR</div>
                                </div>
                                <div className="text-[17px] font-extrabold tracking-[-0.3px]">{currentLocation.emr}</div>
                                <div className="text-[12.5px] text-mapper-muted-2">Source of appointments &amp; records</div>
                            </div>

                            {/* arrows 1 */}
                            <div className="w-[78px] shrink-0 flex flex-col items-center justify-center gap-2 px-1">
                                <div className="flex items-center w-full text-mapper-teal">
                                    <div className="flex-1 h-[2px]" style={{ background: 'linear-gradient(90deg,#C9E5E0,#0E8577)' }} />
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0E8577" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M5 12h14M13 6l6 6-6 6" />
                                    </svg>
                                </div>
                                <div className="text-[10.5px] font-semibold text-mapper-teal text-center leading-[1.2]">appointments<br />both ways</div>
                                <div className="flex items-center w-full text-mapper-purple">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6A62B5" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M19 12H5M11 6l-6 6 6 6" />
                                    </svg>
                                    <div className="flex-1 h-[2px]" style={{ background: 'linear-gradient(90deg,#6A62B5,#DAD6EE)' }} />
                                </div>
                            </div>

                            {/* Advital Sync node */}
                            <div className="flex-[1.05] min-w-0 border-[1.5px] border-mapper-teal-border-2 bg-mapper-teal-bg-2 rounded-[14px] px-[18px] py-4">
                                <div className="flex items-center gap-[9px] mb-[10px]">
                                    <div className="w-[26px] h-[26px] rounded-[7px] bg-mapper-teal flex items-center justify-center">
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="6" cy="6" r="2.4" /><circle cx="18" cy="18" r="2.4" />
                                            <path d="M8.4 6H14a4 4 0 0 1 4 4v5.5" /><path d="M15.6 18H10a4 4 0 0 1-4-4V8.5" />
                                        </svg>
                                    </div>
                                    <div className="text-sm font-extrabold text-mapper-teal-hover">Advital Sync</div>
                                </div>
                                {entityKeys.map(key => {
                                    const s = stats[key];
                                    const mapped = s?.approved ?? 0;
                                    const total = s?.total ?? 0;
                                    const done = total > 0 && mapped >= total;
                                    return (
                                        <div key={key} className="flex items-center justify-between py-1 text-[12.5px]">
                                            <span className="text-mapper-body-text-3 font-medium">{ENTITY_TITLES[key]}</span>
                                            <span className={`font-mono text-[11.5px] font-semibold ${done ? 'text-mapper-teal-dark' : 'text-mapper-amber'}`}>
                                                {statsLoading ? '…' : `${mapped}/${total}`}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* arrows 2 */}
                            <div className="w-[78px] shrink-0 flex flex-col items-center justify-center gap-2 px-1">
                                <div className="flex items-center w-full text-mapper-teal">
                                    <div className="flex-1 h-[2px]" style={{ background: 'linear-gradient(90deg,#C9E5E0,#0E8577)' }} />
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0E8577" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M5 12h14M13 6l6 6-6 6" />
                                    </svg>
                                </div>
                                <div className="text-[10.5px] font-semibold text-mapper-muted-3 text-center leading-[1.2]">synced<br />calendars</div>
                                <div className="flex items-center w-full text-mapper-purple">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6A62B5" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M19 12H5M11 6l-6 6 6 6" />
                                    </svg>
                                    <div className="flex-1 h-[2px]" style={{ background: 'linear-gradient(90deg,#6A62B5,#DAD6EE)' }} />
                                </div>
                            </div>

                            {/* CRM node */}
                            <div className="flex-1 min-w-0 border-[1.5px] border-mapper-border-2 bg-mapper-surface-2 rounded-[14px] p-4">
                                <div className="flex items-center gap-[10px] mb-1">
                                    <div className="w-[30px] h-[30px] rounded-lg bg-mapper-gray-bg flex items-center justify-center">
                                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#48505E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M3 21V8l9-5 9 5v13" /><path d="M9 21v-6h6v6" />
                                        </svg>
                                    </div>
                                    <div className="text-xs font-bold text-mapper-body-text tracking-[0.4px]">{labels.system}</div>
                                </div>
                                <div className="text-[17px] font-extrabold tracking-[-0.3px] truncate">{currentLocation.name}</div>
                                <div className="text-[12.5px] text-mapper-muted-2">{labels.isAvEmr ? 'Services, providers & booking' : 'Calendars, users & booking'}</div>
                            </div>
                        </div>
                    </div>

                    {/* COVERAGE CARDS */}
                    <div className="flex items-center justify-between mb-[14px]">
                        <h2 className="text-base font-bold m-0">Connection progress</h2>
                        <span className="text-[12.5px] text-mapper-muted-2">Click a card to review its mappings</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        {entityKeys.map(key => {
                            const s = stats[key];
                            const mapped = s?.approved ?? 0;
                            const total = s?.total ?? 0;
                            const pending = s?.pending ?? 0;
                            const rejected = s?.rejected ?? 0;
                            const pct = total > 0 ? Math.round((mapped / total) * 100) : 0;
                            const full = total > 0 && mapped >= total;
                            return (
                                <div
                                    key={key}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => onNavigate(key)}
                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNavigate(key); } }}
                                    className="text-left bg-mapper-white border border-mapper-border rounded-[15px] px-[22px] py-5 block cursor-pointer transition-shadow hover:shadow-[0_1px_3px_rgba(24,33,48,.1)]"
                                >
                                    <div className="flex items-start justify-between mb-4">
                                        <div>
                                            <div className="text-[15px] font-bold tracking-[-0.2px]">{ENTITY_TITLES[key]}</div>
                                            <div className="text-[12.5px] text-mapper-muted-2 mt-[1px]">{entitySubs[key]}</div>
                                        </div>
                                        <div className="flex items-baseline gap-1">
                                            <button
                                                type="button"
                                                title="View approved mappings"
                                                onClick={e => { e.stopPropagation(); onNavigate(key, 'approved'); }}
                                                className={`text-[22px] font-extrabold tracking-[-0.5px] hover:underline underline-offset-2 ${full ? 'text-mapper-teal-dark' : 'text-mapper-text'}`}
                                            >
                                                {statsLoading ? '…' : mapped}
                                            </button>
                                            <button
                                                type="button"
                                                title="View all mappings"
                                                onClick={e => { e.stopPropagation(); onNavigate(key); }}
                                                className="text-sm font-semibold text-mapper-muted-5 hover:underline underline-offset-2"
                                            >
                                                / {statsLoading ? '…' : total}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="h-[7px] bg-mapper-gray-bg rounded-full overflow-hidden mb-3">
                                        <div
                                            className={`h-full rounded-full ${full ? 'bg-mapper-green-dot' : 'bg-mapper-teal'}`}
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                    <div className="text-[11.5px] text-mapper-muted-2 mb-2">{statsLoading ? '…' : `${mapped} of ${total} connected`}</div>
                                    <div className="flex gap-2">
                                        {pending > 0 && (
                                            <button
                                                type="button"
                                                title="View pending mappings"
                                                onClick={e => { e.stopPropagation(); onNavigate(key, 'pending'); }}
                                                className="text-[11.5px] font-semibold px-[9px] py-[3px] rounded-full bg-mapper-amber-bg text-mapper-amber transition-shadow hover:shadow-[0_0_0_2px_rgba(214,158,46,.35)]"
                                            >
                                                {pending} pending
                                            </button>
                                        )}
                                        {rejected > 0 && (
                                            <button
                                                type="button"
                                                title="View rejected mappings"
                                                onClick={e => { e.stopPropagation(); onNavigate(key, 'rejected'); }}
                                                className="text-[11.5px] font-semibold px-[9px] py-[3px] rounded-full bg-mapper-red-bg text-mapper-red transition-shadow hover:shadow-[0_0_0_2px_rgba(178,58,78,.3)]"
                                            >
                                                {rejected} rejected
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};
